import * as changeCase from 'change-case';
import dayjs from 'dayjs';
import { dequal as isEqual } from 'dequal';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { mainRoute } from '@src/utils/consts/routesMainMenu.const';

import { fetchReportData } from '@api/analytics';
import {
  createFilter,
  fetchFilterById,
  updateFilter,
} from '@api/analytics/filters';

import PageContainer from '@components/commons/containerPage.common';

import { useAnalytics } from '@contexts/analyticsContext';

import { scrollToTop } from '@utils';

import AnalyticsTable from './_components/analyticsTable';
import DynamicChart, { MAX_CHART_LINES } from './_components/dynamicChart';
import FilterCustomizationPanel from './_components/filterCustomizationPanel';
import ReportHeader from './_components/reportHeader';
import ShareButton from './_components/shareButton';
import { formatRawData, useChartData } from './_utils/chartHelpers';
import { applyFiltersToReportConfig } from './_utils/filtersHelpers';
import LocalizedThemeProvider from './_utils/localizedThemeProvider';

const PAGES_AND_SCREEN_TEMPLATE = {
  name: 'Pages and Screens',
  description: '',
  dimensions: [
    { name: 'pageTitle' },
    { name: 'unifiedPagePathScreen' },
    { name: 'eventName' },
    { name: 'date' },
  ],
  metrics: [
    { name: 'screenPageViews' },
    { name: 'eventCount' },
    { name: 'activeUsers' },
    { name: 'userEngagementDuration' },
  ],
  orderBys: [{ metric: { metricName: 'screenPageViews' }, desc: true }],
  filterCriteria: [],
  timeSeriesMetric: 'screenPageViews',
  timeSeriesDimension: 'unifiedPagePathScreen',
};
const EVENTS = {
  name: 'Top Events',
  description: '',
  dimensions: [{ name: 'eventName' }, { name: 'date' }],
  metrics: [{ name: 'eventCount' }],
  orderBys: [{ metric: { metricName: 'eventCount' }, desc: true }],
  filterCriteria: [],
  timeSeriesMetric: 'eventCount',
  timeSeriesDimension: 'eventName',
};
const DEFAULTS = {
  name: 'Detailed Data',
  description: '',
  dimensions: [{ name: 'date' }],
  metrics: [],
  orderBys: [],
  filterCriteria: [],
  timeSeriesMetric: null,
  timeSeriesDimension: null,
};

function DetailedPanelPage({ panelType: panelTypeFromProps }) {
  const navigate = useNavigate();
  const {
    propertyId,
    panelType: panelTypeFromParams,
    filterId: filterIdFromParams,
  } = useParams();
  const {
    firstInit,
    setFirstInit,
    authToken,
    startDate,
    endDate,
    selectedProperty,
    changeSelectedProperty,
    properties,
    loadingProperties,
    metadata,
    // loadingMetadata,
    // metadataError,
  } = useAnalytics();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailedReportData, setDetailedReportData] = useState(null);
  const [liveReportData, setLiveReportData] = useState(null);
  const [rawReportData, setRawReportData] = useState(null);
  const [selectedRowsForChart, setSelectedRowsForChart] = useState([]);

  const [originalFilterConfig, setOriginalFilterConfig] = useState(null);
  const [customFilterConfig, setCustomFilterConfig] = useState(null);
  const [liveFilterConfig, setLiveFilterConfig] = useState(null);
  const [isConfigEqual, setIsConfigEqual] = useState(true);

  const [filterId, setFilterId] = useState(filterIdFromParams);
  const [isEditFilter, setIsEditFilter] = useState(false);
  const [isNewFilter, setIsNewFilter] = useState(false);
  const [isCustomFilterPanelOpen, setIsCustomFilterPanelOpen] = useState(false);

  const panelType = useMemo(
    () => panelTypeFromProps || panelTypeFromParams,
    [panelTypeFromProps, panelTypeFromParams],
  );
  const isCustomFilterMode = useMemo(
    () => panelType === 'custom-filter',
    [panelType],
  );

  const tableDimensions = useMemo(() => {
    return customFilterConfig && metadata
      ? customFilterConfig.dimensions.map((dim) => {
          const dimensionMetadata = metadata.dimensions[dim.name];
          return {
            name: dim.name,
            label: dimensionMetadata?.uiName || dim.name,
          };
        })
      : [];
  }, [metadata, customFilterConfig?.dimensions]);

  const tableMetrics = useMemo(() => {
    return customFilterConfig && metadata
      ? customFilterConfig.metrics.map((metric) => {
          const metricMetadata = metadata.metrics[metric.name];
          return {
            name: metric.name,
            label: metricMetadata?.uiName || metric.name,
          };
        })
      : [];
  }, [metadata, customFilterConfig?.metrics]);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (firstInit === null) setFirstInit(true);
  }, [firstInit]);

  useEffect(() => {
    if (isEqual(customFilterConfig, originalFilterConfig)) {
      setIsConfigEqual(true);
    } else {
      setIsConfigEqual(false);
    }
  }, [originalFilterConfig, customFilterConfig]);

  useEffect(() => {
    if (isCustomFilterMode && filterIdFromParams === 'new') {
      setFilterId(filterIdFromParams);
      setIsNewFilter(true);
      setIsEditFilter(true);
      setIsCustomFilterPanelOpen(true);
    }
  }, [isCustomFilterMode, filterIdFromParams]);

  useEffect(() => {
    if (!loadingProperties && properties?.length && propertyId) {
      // Check if the currently selected property matches the URL parameter
      if (!selectedProperty || selectedProperty.propertyId !== propertyId) {
        // If not, try to find the property in the list of available properties
        const propertyFromUrl = properties.find(
          (p) => p.propertyId === propertyId,
        );

        if (propertyFromUrl) {
          changeSelectedProperty(propertyFromUrl);
        } else {
          toast.error(
            `Property with ID "${propertyId}" not found or accessible.`,
          );
          // navigate('/analytics');
        }
      }
    }
  }, [
    loadingProperties,
    properties,
    propertyId,
    selectedProperty,
    changeSelectedProperty,
  ]);

  useEffect(() => {
    if (isCustomFilterMode && authToken && filterId) {
      setLoading(true);
      setError(null);

      // Logic to handle 'new' filter creation
      if (filterId === 'new') {
        const newFilterTemplate = PAGES_AND_SCREEN_TEMPLATE;
        setCustomFilterConfig(newFilterTemplate);
        setLiveFilterConfig(newFilterTemplate);
        setIsNewFilter(true);
        setLoading(false);
        return;
      }
      // Logic to fetch an existing filter
      const fetchExistingFilter = async () => {
        try {
          const filter = await fetchFilterById({
            token: authToken,
            filterId,
          });
          setOriginalFilterConfig(filter);
          setCustomFilterConfig(filter);
          setLiveFilterConfig(filter);
          setIsNewFilter(false);
        } catch (err) {
          setError('Failed to fetch filter. Please try again.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchExistingFilter();
      return;
    }

    // standard (static filters)
    const newFilterTemplate =
      panelType === 'pages-and-screens'
        ? PAGES_AND_SCREEN_TEMPLATE
        : panelType === 'events'
        ? EVENTS
        : DEFAULTS;
    setCustomFilterConfig(newFilterTemplate);
    setLiveFilterConfig(newFilterTemplate);
    setFilterId(null);
    setIsNewFilter(false);
    setIsEditFilter(false);
    setIsCustomFilterPanelOpen(false);
    setLoading(false);
  }, [isCustomFilterMode, filterId, authToken]);

  const formatDateRangeForTitle = (start, end) => {
    if (!start || !end) return '';
    const startObj = dayjs(start);
    const endObj = dayjs(end);

    if (startObj.isSame(endObj, 'day')) {
      return startObj.format('MMM D, YYYY');
    }
    if (startObj.year() === endObj.year()) {
      if (startObj.month() === endObj.month()) {
        return `${startObj.format('MMM D')} - ${endObj.format('D, YYYY')}`;
      }
      return `${startObj.format('MMM D')} - ${endObj.format('MMM D, YYYY')}`;
    }
    return `${startObj.format('MMM D, YYYY')} - ${endObj.format(
      'MMM D, YYYY',
    )}`;
  };

  const currentPeriod = formatDateRangeForTitle(startDate, endDate);

  // Determine dimensions and metrics based on panelType
  const getReportConfig = React.useCallback(
    (type) => {
      switch (type) {
        case 'pages-and-screens':
          return PAGES_AND_SCREEN_TEMPLATE;
        case 'events':
          return EVENTS;
        case 'custom-filter':
          if (customFilterConfig) {
            let gaFilterRequest = {
              ...customFilterConfig,
              propertyId,
              dateRanges: [
                {
                  startDate: startDate,
                  endDate: endDate,
                },
              ],
            };

            // Apply filter criteria to the report config
            gaFilterRequest = applyFiltersToReportConfig(
              gaFilterRequest,
              customFilterConfig.filterCriteria || [],
            );

            return gaFilterRequest;
          }

          return {
            ...PAGES_AND_SCREEN_TEMPLATE,
            name: PAGES_AND_SCREEN_TEMPLATE.name,
            propertyId,
            dateRanges: [
              {
                startDate: startDate,
                endDate: endDate,
              },
            ],
          };

        default:
          return {
            ...DEFAULTS,
            propertyId,
            dateRanges: [
              {
                startDate: startDate,
                endDate: endDate,
              },
            ],
          };
      }
    },
    [customFilterConfig, startDate, endDate],
  );

  const reportConfig = useMemo(
    () => getReportConfig(panelType),
    [panelType, getReportConfig],
  );

  useEffect(() => {
    const loadDetailedReport = async () => {
      setLoading(true);
      setError(null);
      setDetailedReportData(null);
      setLiveReportData(null);
      setRawReportData(null);
      setSelectedRowsForChart([]);

      try {
        const rawOnly = true;
        let filterConfig = {
          propertyId,
          dateRanges: [{ startDate: startDate, endDate: endDate }],
          dimensions: reportConfig.dimensions,
          metrics: reportConfig.metrics,
          orderBys: reportConfig.orderBys,
          rawOnly,
        };
        if (customFilterConfig?.filterCriteria?.length) {
          filterConfig = applyFiltersToReportConfig(
            filterConfig,
            customFilterConfig.filterCriteria,
          );
        }

        const fetchedData = await fetchReportData({
          propertyId,
          token: authToken,
          filter: filterConfig,
        });

        const reportData = rawOnly
          ? formatRawData({
              rawData: fetchedData.raw,
              selectedDimension: tableDimensions[0],
              withCTR: true,
            })
          : fetchedData.formattedReport;
        setDetailedReportData(reportData);
        setLiveReportData(reportData);
        setRawReportData(fetchedData.raw);
      } catch (err) {
        setError(err.message);
        toast.error(`Failed to load report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (
      !loadingProperties &&
      !(
        !authToken ||
        !propertyId ||
        !startDate ||
        !endDate ||
        !reportConfig.dimensions.length ||
        !tableDimensions.length ||
        (isCustomFilterMode && !customFilterConfig)
      )
    ) {
      loadDetailedReport();
    }
  }, [
    authToken,
    propertyId,
    panelType,
    startDate,
    endDate,
    reportConfig,
    customFilterConfig,
    isCustomFilterMode,
    loadingProperties,
    tableDimensions,
  ]);

  const toggleCustomFilterPanel = async () => {
    setIsCustomFilterPanelOpen(!isCustomFilterPanelOpen);
  };

  // Handle checkbox change for chart selection
  const handleCheckboxChange = async (item, isChecked) => {
    setSelectedRowsForChart((prevSelected) => {
      if (isChecked) {
        // Add if not already present and limit not exceeded
        if (
          prevSelected.length < MAX_CHART_LINES &&
          !prevSelected.some((row) => row.name === item.name)
        ) {
          return [...prevSelected, item];
        } else if (prevSelected.length >= MAX_CHART_LINES) {
          toast.info(
            `You can select a maximum of ${MAX_CHART_LINES} items for the chart.`,
          );
        }
        return prevSelected;
      } else {
        // Remove if unchecked
        return prevSelected.filter((row) => row.name !== item.name);
      }
    });
  };

  // This handler now updates the temporary, live state
  const handleLiveFilterConfigChange = async (config) => {
    setLiveFilterConfig(config);
  };

  const handleEditFilter = async () => {
    setIsEditFilter(!isEditFilter);
    setIsCustomFilterPanelOpen(!isCustomFilterPanelOpen);
  };

  const handleCancelChanges = async () => {
    if (isNewFilter) {
      navigate(`/analytics/${selectedProperty.propertyId}`);
      return;
    }

    setCustomFilterConfig(originalFilterConfig);
    setLiveFilterConfig(originalFilterConfig);
    setIsNewFilter(false);
    setIsEditFilter(false);
    setIsCustomFilterPanelOpen(false);
    navigate(
      `/analytics/${selectedProperty.propertyId}/detail/custom-filter/${filterId}`,
    );
  };

  // Handler for saving a new or updating an existing filter.
  const handleSaveFilter = async () => {
    if (!liveFilterConfig.name.trim()) {
      toast.error('Filter name is required.');
      return;
    }
    setLoading(true);
    try {
      if (isNewFilter) {
        const newFilter = await createFilter({
          token: authToken,
          filterData: liveFilterConfig,
        });
        toast.success('Filter created successfully!');
        setOriginalFilterConfig(newFilter);
        setCustomFilterConfig(newFilter);
        setLiveFilterConfig(newFilter);
        setIsNewFilter(false);
        setIsEditFilter(false);
        setIsCustomFilterPanelOpen(false);
        setFilterId(newFilter._id);
        setLoading(false);
        navigate(
          `/analytics/${propertyId}/detail/custom-filter/${newFilter._id}`,
        );
        return;
      } else {
        const updatedFilter = await updateFilter({
          token: authToken,
          filterId: liveFilterConfig._id,
          updatedData: liveFilterConfig,
        });
        toast.success('Filter updated successfully!');
        setCustomFilterConfig(updatedFilter);
        setLiveFilterConfig(updatedFilter);
        setIsNewFilter(false);
        setIsCustomFilterPanelOpen(false);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save filter.');
      console.error('Error saving filter:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelFilter = async () => {
    if (isNewFilter) {
      const newFilterTemplate = PAGES_AND_SCREEN_TEMPLATE;
      setCustomFilterConfig(newFilterTemplate);
      setLiveFilterConfig(newFilterTemplate);
      setIsNewFilter(true);
      setIsEditFilter(false);
      setIsCustomFilterPanelOpen(false);
      navigate(`/analytics/${selectedProperty.propertyId}`);
      return;
    }

    setCustomFilterConfig(originalFilterConfig);
    setLiveFilterConfig(originalFilterConfig);
    setIsNewFilter(false);
    setIsEditFilter(false);
    setIsCustomFilterPanelOpen(false);
    navigate(
      `/analytics/${selectedProperty.propertyId}/detail/custom-filter/${filterId}`,
    );
  };
  const handleApplyFilter = async () => {
    if (!liveFilterConfig.name.trim()) {
      toast.error('Filter name is required.');
      return;
    }
    setCustomFilterConfig(liveFilterConfig);
    toast.info('Applying filter...');
  };

  const handleLiveDataChange = async (newLiveData) => {
    setLiveReportData(newLiveData);
    setSelectedRowsForChart([]);
  };

  const { chartData, totalDataForSelectedItems } = useChartData({
    data: detailedReportData,
    reportConfig,
    maxChartLines: MAX_CHART_LINES,
    selectedRowsForChart,
    onSelectedRowsForChart: setSelectedRowsForChart,
  });

  // Define breadcrumb items dynamically
  const breadcrumbItems = [
    { label: 'MISC', path: '#' },
    { label: 'ANALYTICS', path: mainRoute.MISC_ANALYTICS },
    {
      label: selectedProperty?.displayName || 'Loading Property...',
      path: selectedProperty
        ? `${mainRoute.MISC_ANALYTICS}/${selectedProperty.propertyId}`
        : undefined,
    },
    {
      label: reportConfig.name,
    },
  ];

  const renderTitle = () => {
    if (isCustomFilterMode && isEditFilter) {
      return (
        <TextField
          variant='standard'
          value={liveFilterConfig?.name || ''}
          onChange={(e) =>
            handleLiveFilterConfigChange({
              ...liveFilterConfig,
              name: e.target.value,
            })
          }
          placeholder='Filter Title'
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '1.5rem',
              width: 'fit-content',
            },
          }}
        />
      );
    }

    if (isCustomFilterMode) {
      return (
        <Typography variant='h5' component='h2' gutterBottom>
          {liveFilterConfig?.name}
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: 'inline-flex',
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant='h5' component='h2' gutterBottom>
          {reportConfig.name} Report
        </Typography>
        {reportConfig && detailedReportData?.formattedMappedRows ? (
          <ShareButton
            iconOnly
            data={detailedReportData.formattedMappedRows}
            fileName={changeCase.kebabCase(
              `${reportConfig.name} ${currentPeriod}`,
            )}
            title={`${reportConfig.name} (${currentPeriod})`}
            sx={{
              mb: '0.35em',
            }}
          />
        ) : null}
      </Box>
    );
  };

  return (
    <LocalizedThemeProvider>
      <PageContainer breadcrumbItems={breadcrumbItems} childrenSx={{ p: 3 }}>
        <Box maxWidth='xl' sx={{ mt: 4, mb: 4 }}>
          <ReportHeader
            // withCustomFilterPanel={isEditFilter}
            isCustomFilterPanelOpen={isNewFilter || isEditFilter}
            onToggleFilterPanel={toggleCustomFilterPanel}
            sx={{ mr: 3 }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 4,
              mb: 2,
            }}
          >
            {renderTitle()}
            {isCustomFilterMode && (
              <Box
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
              >
                {isEditFilter ? (
                  <>
                    <Button
                      variant='outlined'
                      onClick={handleCancelChanges}
                      disabled={loading}
                    >
                      {isNewFilter ? 'Cancel' : 'Done'}
                    </Button>
                    <Button
                      variant='contained'
                      onClick={handleSaveFilter}
                      disabled={isConfigEqual || loading}
                    >
                      {isNewFilter ? 'Create' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant='contained'
                    onClick={handleEditFilter}
                    disabled={loading}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Main content grid */}
          <Grid container spacing={3}>
            {/* The main content area with the chart and table */}
            <Grid
              item
              xs={12}
              md={isCustomFilterPanelOpen ? 8 : 12}
              sx={{ pr: isCustomFilterPanelOpen ? undefined : 3 }}
            >
              {/* Chart Section */}
              <Grid item xs={12} sx={{ mb: 4 }}>
                {loadingProperties ? (
                  <Box display='flex' justifyContent='center' py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                    {loading ? (
                      <Box display='flex' justifyContent='center' py={4}>
                        <CircularProgress />
                      </Box>
                    ) : error ? (
                      <Box display='flex' justifyContent='center' py={4}>
                        <Alert severity='error'>
                          Error loading report: {error}
                        </Alert>
                      </Box>
                    ) : (detailedReportData?.rows?.length || 0) === 0 ? (
                      <Box display='flex' justifyContent='center' py={4}>
                        <Alert severity='info'>
                          No data available for this report in the selected
                          period.
                        </Alert>
                      </Box>
                    ) : (
                      <>
                        <Typography variant='h6' gutterBottom>
                          {reportConfig.name} ({currentPeriod})
                        </Typography>
                        {/* Only show "Select items" message if BOTH individual and total data (of selected) are empty */}
                        {selectedRowsForChart.length === 0 &&
                        totalDataForSelectedItems.length === 0 ? (
                          <Box
                            sx={{
                              height: 300,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.secondary',
                            }}
                          >
                            <Typography variant='body1'>
                              Select up to {MAX_CHART_LINES} items from the
                              table below to compare their trends.
                            </Typography>
                          </Box>
                        ) : (
                          <DynamicChart
                            reportConfig={reportConfig}
                            chartData={chartData}
                            selectedItems={selectedRowsForChart}
                            totalDataForSelectedItems={
                              totalDataForSelectedItems
                            }
                          />
                        )}
                      </>
                    )}
                  </Paper>
                )}
              </Grid>

              {/* Table Section */}
              <Grid item xs={12}>
                {loadingProperties ? null : (
                  <Paper elevation={3} sx={{ height: '100%' }}>
                    {loading ? (
                      <Box display='flex' justifyContent='center' py={4}>
                        <CircularProgress />
                      </Box>
                    ) : error ? (
                      <Box display='flex' justifyContent='center' py={4}>
                        <Alert severity='error'>
                          Error loading report: {error}
                        </Alert>
                      </Box>
                    ) : (detailedReportData?.rows?.length || 0) === 0 ? (
                      <Box display='flex' justifyContent='center' py={4}>
                        <Alert severity='info'>
                          No data available for this report in the selected
                          period.
                        </Alert>
                      </Box>
                    ) : (
                      <AnalyticsTable
                        data={liveReportData}
                        rawData={rawReportData}
                        dimensions={tableDimensions}
                        metrics={tableMetrics}
                        reportConfig={reportConfig}
                        // onMetricsFilterChange={setTableMetrics}
                        selectedRowsForChart={selectedRowsForChart}
                        onCheckboxChange={handleCheckboxChange}
                        onLiveDataChange={handleLiveDataChange}
                      />
                    )}
                  </Paper>
                )}
              </Grid>
            </Grid>

            {/* Right-side panel for custom filter UI */}
            {isCustomFilterMode && isCustomFilterPanelOpen && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{ pr: isCustomFilterPanelOpen ? 3 : undefined }}
              >
                {loadingProperties ? (
                  <Box display='flex' justifyContent='center' py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Paper elevation={3} sx={{ p: 3 }}>
                    {!liveFilterConfig ? (
                      <Box display='flex' justifyContent='center' py={4}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <FilterCustomizationPanel
                        filterConfig={liveFilterConfig}
                        onConfigChange={handleLiveFilterConfigChange}
                        onCancelFilter={handleCancelFilter}
                        onApplyFilter={handleApplyFilter}
                        isNewFilter={isNewFilter}
                      />
                    )}
                  </Paper>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      </PageContainer>
    </LocalizedThemeProvider>
  );
}

export default DetailedPanelPage;
